import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import {
  Select,
  Table,
  Button,
  Typography,
  Spin,
  Card,
  Space,
  Alert,
  Popconfirm,
  Tooltip,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import FileUploadField from './FileUploadField'; // adjust path if needed
import type { RcFile } from 'antd/es/upload';
import type { MediaUploadConfig } from '../../utils/file.util';
import { useTranslation } from 'react-i18next';
import BulkImportSummaryPreview from './BulkImportSummaryPreview';
import { Summary, FailedRecord } from './BulkImportSummaryPreview';
import { CloudUploadOutlined } from '@ant-design/icons';
import { normalizeBooleanValue } from '../../utils/boolean.util';

const { Option } = Select;
const { Title } = Typography;

type CSVRow = Record<string, string | boolean>;

/**
 * Helper function to normalize boolean-like values in a CSV row
 */
const normalizeCSVRowData = (row: CSVRow): CSVRow => {
  const normalizedRow: CSVRow = {};

  for (const [key, value] of Object.entries(row)) {
    const normalized = normalizeBooleanValue(value);
    normalizedRow[key] = typeof normalized === 'boolean' ? normalized : (normalized as string);
  }

  return normalizedRow;
};

interface TemplateOption {
  id: string;
  name: string;
  mappingTemplate: Record<string, string>;
}

interface CSVMappingToolProps<T> {
  mappingStructure: Record<string, string>; // e.g., { name: 'Name', email: 'Email' }
  onComplete: (mappedData: CSVRow[]) => void;
  uploadConfig: MediaUploadConfig;
  previewRowCount?: number;
  className?: string;
  isLoading?: boolean;
  summary?: Summary;
  showSummary?: boolean;
  failedRecords?: FailedRecord<T>[];
  optionalFields?: string[]; // Array of field keys that are optional
  // Template-related props
  templates?: TemplateOption[];
  templatesLoading?: boolean;
  selectedTemplateId?: string;
  onTemplateSelect?: (templateId: string | null) => void;
  onTemplateDelete?: (templateId: string) => Promise<void>;
  onMappingsChange?: (mappings: Record<string, string>) => void;
  onCsvHeadersChange?: (headers: string[]) => void;
  additionalFields?: React.ReactNode; // Additional fields to render below template selection
}

const CSVImporter = <T,>({
  mappingStructure,
  onComplete,
  uploadConfig,
  previewRowCount = 5,
  className,
  isLoading = false,
  summary,
  showSummary = false,
  failedRecords,
  optionalFields = [],
  templates = [],
  templatesLoading = false,
  selectedTemplateId,
  onTemplateSelect,
  onTemplateDelete,
  onMappingsChange,
  onCsvHeadersChange,
  additionalFields,
}: CSVMappingToolProps<T>) => {
  const [file, setFile] = useState<RcFile | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<CSVRow[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [isParsing, setIsParsing] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const { t } = useTranslation();

  // Notify parent when mappings change
  useEffect(() => {
    if (onMappingsChange) {
      onMappingsChange(mappings);
    }
  }, [mappings, onMappingsChange]);

  // Notify parent when CSV headers change
  useEffect(() => {
    if (onCsvHeadersChange && csvHeaders.length > 0) {
      onCsvHeadersChange(csvHeaders);
    }
  }, [csvHeaders, onCsvHeadersChange]);

  // Step 1: When file is selected, parse first N rows for preview
  useEffect(() => {
    if (!file) return;

    const preview: CSVRow[] = [];
    let headersCaptured = false;
    setIsParsing(true);

    Papa.parse(file as File, {
      header: true,
      skipEmptyLines: true,
      worker: true,
      step: (result, parser) => {
        if (!headersCaptured) {
          const fields = Object.keys(result.data as CSVRow);
          setCsvHeaders(fields);
          headersCaptured = true;
        }

        if (preview.length < previewRowCount) {
          const normalizedRow = normalizeCSVRowData(result.data as CSVRow);
          preview.push(normalizedRow);
        }

        if (preview.length === previewRowCount) {
          parser.abort(); // Stop parsing early for preview
          setPreviewRows(preview);
          setIsParsing(false);
        }
      },
      error: (err) => {
        console.error('CSV parse error', err);
        setIsParsing(false);
      },
      complete: () => {
        // Set preview rows if we haven't already (e.g., if CSV has fewer rows than previewRowCount)
        if (preview.length > 0) {
          setPreviewRows(preview);
        }
        setIsParsing(false);
      },
    });
  }, [file, previewRowCount]);

  const handleMappingChange = (structureKey: string, selectedCsvColumn: string) => {
    setMappings((prev) => {
      const newMappings = {
        ...prev,
        [structureKey]: selectedCsvColumn,
      };
      return newMappings;
    });
    // Clear template error when user manually changes mappings
    if (templateError) {
      setTemplateError(null);
    }
  };

  // Validate template mapping against CSV headers
  const validateTemplateMapping = (templateMapping: Record<string, string>): boolean => {
    const templateValues = Object.values(templateMapping);
    const missingHeaders = templateValues.filter((value) => !csvHeaders.includes(value));
    return missingHeaders.length === 0;
  };

  // Handle template selection
  useEffect(() => {
    if (!selectedTemplateId || !csvHeaders.length) return;

    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
    if (!selectedTemplate) return;

    // Validate template mapping
    if (!validateTemplateMapping(selectedTemplate.mappingTemplate)) {
      setTemplateError(
        t('visaApplications.bulkImport.templateValidationError') ||
          "Template fields don't match CSV headers. Please map manually or select a different template."
      );
      if (onTemplateSelect) {
        onTemplateSelect(null);
      }
      return;
    }

    // Clear error and apply template mappings
    setTemplateError(null);
    setMappings(selectedTemplate.mappingTemplate);
  }, [selectedTemplateId, csvHeaders, templates, t, onTemplateSelect]);

  // Handle template deletion
  const handleTemplateDelete = async (templateId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!onTemplateDelete) return;

    setIsDeletingTemplate(true);
    try {
      await onTemplateDelete(templateId);
      // Clear selection if deleted template was selected
      if (selectedTemplateId === templateId && onTemplateSelect) {
        onTemplateSelect(null);
        setMappings({});
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleParseAll = () => {
    const finalData: CSVRow[] = [];

    Papa.parse(file as File, {
      header: true,
      skipEmptyLines: true,
      worker: true,
      step: (result) => {
        const row = normalizeCSVRowData(result.data as CSVRow);
        const mappedRow: CSVRow = {};

        for (const [structureKey, csvColumn] of Object.entries(mappings)) {
          mappedRow[structureKey] = row[csvColumn] ?? '';
        }

        finalData.push(mappedRow);
      },
      complete: () => {
        onComplete(finalData);
      },
      error: (err) => {
        console.error('CSV parse error during full parse', err);
      },
    });
  };

  const requiredFields = Object.keys(mappingStructure).filter(
    (key) => !optionalFields.includes(key)
  );
  const mappedRequiredFields = requiredFields.filter((key) => mappings[key]);
  const isMappingComplete = mappedRequiredFields.length === requiredFields.length;

  return (
    <Spin spinning={isLoading || isParsing} tip={t('crud.create.uploading') || 'Loading...'}>
      <div className={className}>
        <FileUploadField
          multiple={false}
          uploadConfig={uploadConfig}
          value={file}
          onChange={(f) => setFile(f as RcFile)}
          placeholder={t('crud.create.dragOrUploadCSV')}
        />

        {isParsing && <Spin tip={t('common.parsingCsvPreview')} />}

        {!isParsing && file && csvHeaders.length > 0 && !showSummary && (
          <>
            {/* Template Selection */}
            {templates && templates.length > 0 && (
              <Card className="shadow-lg mt-3">
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="mb-2 d-block">
                      {t('visaApplications.bulkImport.selectTemplate') || 'Select Template'}
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Select
                        style={{ flex: 1 }}
                        placeholder={
                          t('visaApplications.bulkImport.selectTemplatePlaceholder') ||
                          'Select a template'
                        }
                        value={selectedTemplateId}
                        onChange={(value) => {
                          if (onTemplateSelect) {
                            onTemplateSelect(value || null);
                          }
                        }}
                        allowClear
                        disabled={isDeletingTemplate}
                        loading={templatesLoading}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ??
                          false
                        }
                        options={templates.map((template) => ({
                          label: template.name,
                          value: template.id,
                        }))}
                      />
                      {selectedTemplateId && onTemplateDelete && (
                        <Popconfirm
                          title={
                            t('visaApplications.bulkImport.deleteTemplateConfirm') ||
                            'Are you sure you want to delete this template?'
                          }
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            handleTemplateDelete(selectedTemplateId);
                          }}
                          okText={t('common.yes')}
                          cancelText={t('common.no')}
                          disabled={isDeletingTemplate}
                        >
                          <Tooltip title={t('common.delete')}>
                            <DeleteOutlined
                              style={{
                                fontSize: '16px',
                                color: '#ff4d4f',
                                cursor: isDeletingTemplate ? 'not-allowed' : 'pointer',
                                opacity: isDeletingTemplate ? 0.5 : 1,
                              }}
                              onClick={(e) => {
                                if (!isDeletingTemplate) {
                                  e.stopPropagation();
                                }
                              }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Additional Fields (e.g., Visa Status) */}
            {additionalFields && csvHeaders.length > 0 && (
              <div className="mt-3">{additionalFields}</div>
            )}

            {/* Template Validation Error */}
            {templateError && (
              <Alert
                message={templateError}
                type="error"
                showIcon
                closable
                onClose={() => setTemplateError(null)}
                className="mt-3"
              />
            )}

            <Card className="shadow-lg mt-3">
              <div className="row">
                <Title level={4}>{t('crud.create.mapColumns')}</Title>
                {Object.entries(mappingStructure).map(([key, label]) => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="mb-2">
                      {label}
                      {optionalFields.includes(key) && (
                        <span className="text-muted ms-1">(Optional)</span>
                      )}
                    </label>
                    <Select
                      className="w-100"
                      placeholder={t('crud.create.selectCsvColumns')}
                      value={mappings[key]}
                      onChange={(val) => handleMappingChange(key, val)}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    >
                      {optionalFields.includes(key) && <Option value="">Skip this field</Option>}
                      {csvHeaders.map((header) => (
                        <Option key={header} value={header}>
                          {header}
                        </Option>
                      ))}
                    </Select>
                  </div>
                ))}

                <Button
                  className="mt-3"
                  type="primary"
                  size="large"
                  icon={<CloudUploadOutlined />}
                  disabled={!isMappingComplete || !!templateError}
                  onClick={handleParseAll}
                  style={{ marginTop: 16 }}
                >
                  {t('crud.create.confirmMappingAndParseAll')}
                </Button>
              </div>
            </Card>

            {previewRows.length > 0 && Object.keys(mappings).length > 0 && (
              <Card className="shadow-lg mt-3">
                <Title level={5}>{t('common.csvPreview')}</Title>
                <Table
                  dataSource={previewRows.map((row) => {
                    const mappedRow: CSVRow = {};
                    for (const [structureKey, csvColumn] of Object.entries(mappings)) {
                      mappedRow[structureKey] = row[csvColumn] ?? '';
                    }
                    return mappedRow;
                  })}
                  columns={Object.entries(mappingStructure)
                    .filter(([key]) => mappings[key]) // Only show mapped columns
                    .map(([key, label]) => ({
                      title: label,
                      dataIndex: key,
                      key: key,
                    }))}
                  pagination={false}
                  rowKey={(_, idx) => idx?.toString() ?? '0'}
                  scroll={{ x: true }}
                />
              </Card>
            )}
          </>
        )}
        {summary && showSummary && (
          <div style={{ marginTop: 32 }}>
            <BulkImportSummaryPreview<T> summary={summary} failedRecords={failedRecords} />
          </div>
        )}
      </div>
    </Spin>
  );
};

export default CSVImporter;
